<template lang="pug">
div
    v-dialog(v-model="dialog" max-width="900px" @click:outside="close")
        template(v-slot:activator="{ on, attrs }")
            v-btn(color="teal" dark v-bind="attrs" v-on="on" small @click="openDialog")
                v-icon(left) mdi-account-group
                | Members

        v-card
            v-card-title
                span.headline Members: {{ groupName }}
                v-spacer
                v-btn(icon @click="close")
                    v-icon mdi-close

            v-card-text
                v-container(style="padding: 0;")
                    v-row
                        v-col(cols="12")
                            h3 Add New Member
                            v-autocomplete(
                                v-model="selectedUserId"
                                :items="usersList"
                                :loading="isLoadingUsers"
                                :search-input.sync="userSearch"
                                item-text="email"
                                item-value="id"
                                label="Select user to add"
                                clearable
                                prepend-icon="mdi-account-search"
                                no-data-text="Type at least 3 characters to search users"
                                hint="Search by name or email"
                                persistent-hint
                            )
                                template(v-slot:item="{ item }")
                                    v-list-item-avatar
                                        img(:src="item.profile_picture_thumb")
                                    v-list-item-content
                                        v-list-item-title {{ item.fname }} {{ item.lname }}
                                        v-list-item-subtitle {{ item.email }}
                            
                            v-btn(color="primary" @click="addMember" :disabled="!selectedUserId || addingMember" class="mt-2")
                                v-icon(left) mdi-account-plus
                                | Add Member Directly
                    v-divider.my-4

                    v-row
                        v-col(cols="12")
                            v-data-table(
                                :headers="headers"
                                :items="members"
                                :loading="loading"
                                loading-text="Loading members..."
                                no-data-text="No members found"
                            )
                                template(v-slot:item.name="{ item }")
                                    v-avatar(size="28" style="margin-right: 8px;")
                                        img(:src="item.profile_picture_thumb")
                                    | {{ item.fname }} {{ item.lname }}

                                template(v-slot:item.joined_at="{ item }")
                                    span {{ item.joined_at | dateFormat }}

                                template(v-slot:item.actions="{ item }")
                                    v-btn(icon small @click="confirmRemove(item)" color="red")
                                        v-icon mdi-close

            v-card-actions
                v-spacer
                v-btn(text @click="close") Close

            v-snackbar(v-model="snackbar.show" :color="snackbar.color" top)
                | {{ snackbar.message }}

    v-dialog(v-model="removeDialog" max-width="420px")
        v-card
            v-card-title.headline Remove Member
            v-card-text Are you sure you want to remove #[strong {{ removingName }}] from the group?
            v-card-actions
                v-spacer
                v-btn(text @click="removeDialog = false") Cancel
                v-btn(color="red darken-1" text :loading="removing" @click="doRemove") Remove
</template>

<script>
import moment from 'moment';
import { mapActions } from 'vuex';

export default {
    name: 'GroupMembersManager',
    props: {
        groupId: { type: Number, required: true },
        groupName: { type: String, default: '' }
    },
    data() {
        return {
            dialog: false,
            loading: false,
            members: [],
            removeDialog: false,
            removing: false,
            removingId: null,
            removingName: '',
            selectedUserId: null,
            userSearch: null,
            isLoadingUsers: false,
            addingMember: false,
            usersList: [],
            snackbar: {
                show: false,
                message: '',
                color: 'success'
            },
            headers: [
                { text: 'Name', value: 'name', sortable: false },
                { text: 'Email', value: 'email', sortable: false },
                { text: 'Joined', value: 'joined_at', sortable: false },
                { text: 'Remove', value: 'actions', sortable: false, align: 'end' }
            ]
        };
    },
    watch: {
        userSearch(val) {
            if (val && val.length > 2) {
                this.searchUsers(val);
            }
        }
    },
    filters: {
        dateFormat(date) {
            return date ? moment(date).format('lll') : 'n/a';
        }
    },
    methods: {
        async openDialog() {
            this.dialog = true;
            this.loading = true;
            this.members = [];
            try {
                const result = await this.findMembers({
                    query: { group_id: this.groupId, $limit: 200 }
                });
                const memberships = result.data || result;
                const enriched = await Promise.all(
                    memberships.map(async (m) => {
                        try {
                            const user = await this.$store.dispatch('users/get', m.user_id);
                            return {
                                membershipId: m.id,
                                user_id: m.user_id,
                                fname: user.fname,
                                lname: user.lname,
                                email: user.email,
                                profile_picture_thumb: user.profile_picture_thumb,
                                joined_at: m.date_added || m.created_at
                            };
                        } catch {
                            return {
                                membershipId: m.id,
                                user_id: m.user_id,
                                fname: '(Unknown)',
                                lname: '',
                                email: '',
                                profile_picture_thumb: '',
                                joined_at: m.date_added || m.created_at
                            };
                        }
                    })
                );
                this.members = enriched;
            } catch (err) {
                console.error('[GroupMembersManager] Failed to load members:', err);
            } finally {
                this.loading = false;
            }
        },
        close() {
            this.dialog = false;
            this.members = [];
        },
        confirmRemove(item) {
            this.removingId = item.membershipId;
            this.removingUserId = item.user_id;
            this.removingName = `${item.fname} ${item.lname}`.trim() || item.email;
            this.removeDialog = true;
        },
        async doRemove() {
            this.removing = true;
            try {
                if (this.removingId) {
                    await this.removeMember(this.removingId);
                } else {
                    await this.removeMember([null, { query: { user_id: this.removingUserId, group_id: this.groupId } }]);
                }
                this.members = this.members.filter(m => m.user_id !== this.removingUserId);
                this.removeDialog = false;
                this.showSuccess('Member removed successfully');
            } catch (err) {
                console.error('[GroupMembersManager] Failed to remove member:', err);
                this.showError('Failed to remove member: ' + (err.message || err));
            } finally {
                this.removing = false;
            }
        },
        async searchUsers(search) {
            this.isLoadingUsers = true;
            try {
                const result = await this.findUsers({
                    query: {
                        $or: [
                            { email: { $like: `%${search}%` } },
                            { fname: { $like: `%${search}%` } },
                            { lname: { $like: `%${search}%` } }
                        ],
                        $limit: 20
                    }
                });
                this.usersList = result.data || [];
            } catch (error) {
                this.showError('Failed to search users');
            } finally {
                this.isLoadingUsers = false;
            }
        },
        async addMember() {
            if (!this.selectedUserId) return;
            
            this.addingMember = true;
            try {
                await this.createMember({
                    group_id: this.groupId,
                    user_id: this.selectedUserId
                });
                
                this.showSuccess('Member added successfully');
                this.selectedUserId = null;
                this.userSearch = null;
                // Refresh members list
                await this.openDialog();
            } catch (error) {
                this.showError(error.message || 'Failed to add member');
            } finally {
                this.addingMember = false;
            }
        },
        showSuccess(message) {
            this.snackbar = { show: true, message, color: 'success' };
        },
        showError(message) {
            this.snackbar = { show: true, message, color: 'error' };
        },
        ...mapActions('bh-user-to-group', { findMembers: 'find', removeMember: 'remove', createMember: 'create' }),
        ...mapActions('users', { findUsers: 'find' })
    }
};
</script>
